import http from '../httpConfig';
import { API_ENDPOINTS } from '../utils/apiConstant';

export interface SubscriptionListPayload {
  page?: number;
  limit?: number;
  productId?: number;
  status?: string;
  expiryFrom?: string;
  expiryTo?: string;
  orderBy?: string;
  order?: string;
}

export interface SubscriptionFormPayload {
  tenant_id: number;
  product_id: number;
  plan_id: number;
  billing_cycle_id: number;
  start_date: string;
  amount: number;
  currency_id: number;
  auto_reminder?: boolean;
  notes?: string;
}

const list = (payload: SubscriptionListPayload) => {
  const { page, limit, productId, status, expiryFrom, expiryTo, orderBy, order } = payload;
  const params: Record<string, string> = {};
  if (page) params.page = String(page);
  if (limit) params.limit = String(limit);
  if (productId) params.productId = String(productId);
  if (status) params.status = status;
  if (expiryFrom) params.expiryFrom = expiryFrom;
  if (expiryTo) params.expiryTo = expiryTo;
  if (orderBy) params.orderBy = orderBy;
  if (order) params.order = String(order).toLowerCase();
  const query = new URLSearchParams(params).toString();
  return http.get(`${API_ENDPOINTS.subscriptions}${query ? `?${query}` : ''}`);
};

const get = (id: number | string) => http.get(`${API_ENDPOINTS.subscriptions}/${id}`);

const getByTenantId = (tenantId: number | string) => http.get(`${API_ENDPOINTS.subscriptions}/by-tenant/${tenantId}`);

const create = (payload: SubscriptionFormPayload) => http.post(API_ENDPOINTS.subscriptions, payload);

const update = (
  id: number | string,
  payload: Partial<SubscriptionFormPayload> & { end_date?: string },
  confirmEndDateOverride?: boolean,
) =>
  http.patch(`${API_ENDPOINTS.subscriptions}/${id}`, payload, {
    headers: confirmEndDateOverride ? { 'X-Confirm-End-Date-Override': 'true' } : undefined,
  });

const computeEndDate = (billingCycleId: number, startDate: string) =>
  http.get(`${API_ENDPOINTS.subscriptions}/billing-cycles/${billingCycleId}/compute-end-date?startDate=${startDate}`);

export interface RenewSubscriptionPayload {
  amount: number;
  currency_id: number;
  payment_date: string;
  payment_mode: 'cash' | 'bank_transfer' | 'cheque' | 'other';
  reference_no?: string;
  notes?: string;
}

const renew = (id: number | string, payload: RenewSubscriptionPayload) =>
  http.post(`${API_ENDPOINTS.subscriptions}/${id}/renew`, payload);

const schedulePlanChange = (id: number | string, payload: { newPlanId: number; reason?: string }) =>
  http.post(`${API_ENDPOINTS.subscriptions}/${id}/plan-change`, payload);

const cancelPlanChange = (id: number | string) => http.delete(`${API_ENDPOINTS.subscriptions}/${id}/plan-change`);

const SubscriptionService = {
  list,
  get,
  getByTenantId,
  create,
  update,
  computeEndDate,
  renew,
  schedulePlanChange,
  cancelPlanChange,
};

export default SubscriptionService;
